import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../services/api';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Notice, StatusBadge, inputStyle, selectStyle, tabStyle, apiErrors } from '../components/insurance/formBits';
import ClaimStatusBadge from '../components/claims/ClaimStatusBadge';
import { formatMoney } from '../insurance/format';
import { formatLongDate } from '../dateOnly';
import { submitOutcomeText, describeProblems } from '../insurance/claims';

// Insurance → Claims (admin). Tiles, queues, filters, create-from-sessions,
// bulk submit, and a manual status check against Stedi.
const QUEUES = [
  ['all', 'All', {}],
  ['draft', 'Drafts', { status: 'draft' }],
  ['ready', 'Ready', { status: 'ready' }],
  ['awaiting', 'Awaiting payer', { status: 'submitted,accepted,pending' }],
  ['attention', 'Needs attention', { needsAttention: '1' }],
  ['done', 'Paid / closed', { status: 'paid,partially_paid,denied,corrected,voided' }],
];
const th = { padding: '8px 10px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 13 };
const td = { padding: '8px 10px', verticalAlign: 'top', fontSize: 13 };
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function AdminClaimsPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState('all');
  const [filters, setFilters] = useState({ therapistId: '', payerId: '', dosFrom: '', dosTo: '' });
  const [q, setQ] = useState('');            // what is typed in the search box
  const [search, setSearch] = useState('');  // what is sent: the typed text, 300 ms after typing stops
  const [page, setPage] = useState(1);
  const seqRef = useRef(0);                  // only the newest list request may update the table
  const [data, setData] = useState({ items: [], total: 0, pageSize: 50, showAmounts: true });
  const [therapists, setTherapists] = useState([]);
  const [payers, setPayers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [msg, setMsg] = useState(null);           // { tone, lines: [] }
  const [busy, setBusy] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);

  useEffect(() => { if (!localStorage.getItem('token')) navigate('/'); }, [navigate]);

  const loadSummary = useCallback(async () => {
    try { setSummary((await API.get('/billing/claims/summary')).data); }
    catch (err) { setMsg({ tone: 'bad', lines: [apiErrors(err).message] }); }
  }, []);
  const loadList = useCallback(async () => {
    const params = { ...QUEUES.find((x) => x[0] === queue)[2], page, pageSize: 50 };
    for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
    if (search) params.q = search;
    const seq = ++seqRef.current;
    try {
      const r = await API.get('/billing/claims', { params });
      if (seq !== seqRef.current) return;      // a newer request went out while this one was in flight
      setData(r.data); setSelected(new Set());
    } catch (err) { if (seq === seqRef.current) setMsg({ tone: 'bad', lines: [apiErrors(err).message] }); }
  }, [queue, page, filters, search]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    Promise.all([API.get('/billing/therapists'), API.get('/billing/payers')])
      .then(([t, p]) => { setTherapists(t.data || []); setPayers((p.data || []).filter((x) => x.is_active)); })
      .catch(() => {});
  }, []);
  const refresh = () => { loadSummary(); loadList(); };
  const setFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };
  const pickQueue = (key) => { setQueue(key); setPage(1); };

  const readyRows = useMemo(() => data.items.filter((c) => c.status === 'ready'), [data.items]);
  const allReadySelected = readyRows.length > 0 && readyRows.every((c) => selected.has(c.id));
  const toggleAllReady = () => setSelected(allReadySelected ? new Set() : new Set(readyRows.map((c) => c.id)));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function submitSelected() {
    const claimIds = [...selected];
    if (!claimIds.length) return;
    setBusy(true); setMsg(null);
    try {
      const r = await API.post('/billing/claims/submit', { claimIds });
      const results = r.data.results || [];
      setMsg({ tone: results.length && results.every((x) => x.outcome === 'submitted') ? 'good' : 'warn', lines: results.map(submitOutcomeText) });
      refresh();
    } catch (err) { setMsg({ tone: 'bad', lines: [apiErrors(err).message] }); }
    finally { setBusy(false); }
  }
  async function syncAll() {
    setBusy(true); setMsg(null);
    try {
      const r = await API.post('/billing/claims/sync');
      setMsg({ tone: 'good', lines: [`Checked ${plural(r.data.checked, 'claim')} with Stedi: ${plural(r.data.updated, 'status change')}, ${plural(r.data.events, 'new event')}.`] });
      refresh();
    } catch (err) { setMsg({ tone: 'bad', lines: [apiErrors(err).message] }); }
    finally { setBusy(false); }
  }

  const tiles = summary ? [
    ['Drafts', summary.drafts, 'draft'], ['Ready to submit', summary.ready, 'ready'], ['Awaiting payer', summary.awaiting_payer, 'awaiting'],
    ['Needs attention', summary.needs_attention, 'attention'], ['Paid this month', formatMoney(summary.paid_this_month), null], ['Outstanding', formatMoney(summary.outstanding_ar), null],
  ] : [];
  const counts = summary ? { draft: summary.drafts, ready: summary.ready, awaiting: summary.awaiting_payer, attention: summary.needs_attention } : {};
  const pages = Math.max(1, Math.ceil((data.total || 0) / (data.pageSize || 50)));
  const columns = ['Client', 'Date of service', 'CPT', 'Payer', 'Therapist', data.showAmounts ? 'Charge' : null, 'Status', 'Days', 'Next'].filter(Boolean);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary-color)' }}>Claims</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Insurance claims from completed sessions. <Link to="/admin/insurance-tools">Insurance Tools</Link> · <Link to="/admin/payers">Payers</Link>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" disabled={busy} onClick={syncAll}>Check status with Stedi</Button>
          <Button variant="primary" onClick={() => setShowBuilder((v) => !v)}>{showBuilder ? 'Hide session list' : 'Create claims from sessions'}</Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, margin: '14px 0' }}>
        {tiles.map(([label, value, q]) => (
          <Card key={label} role={q ? 'button' : undefined} tabIndex={q ? 0 : undefined} aria-label={q ? `Show ${label.toLowerCase()}` : undefined}
                onClick={q ? () => pickQueue(q) : undefined} onKeyDown={q ? (e) => { if (e.key === 'Enter' || e.key === ' ') pickQueue(q); } : undefined}
                style={{ padding: '12px 14px', cursor: q ? 'pointer' : 'default', border: q && queue === q ? '2px solid var(--primary-color)' : '2px solid transparent' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: label === 'Needs attention' && Number(value) > 0 ? 'var(--error-color)' : 'var(--text-strong)' }}>{value}</div>
          </Card>
        ))}
      </div>

      {msg && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone={msg.tone}>{msg.lines.length === 1 ? msg.lines[0] : <ul style={{ margin: 0, paddingLeft: 18 }}>{msg.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>}</Notice>
        </div>
      )}

      {showBuilder && <SessionBuilder onCreated={(n, lines) => { setMsg({ tone: n ? 'good' : 'warn', lines }); refresh(); }} />}

      <Card>
        <div style={{ borderBottom: '1px solid var(--border-color)', marginBottom: 10, display: 'flex', flexWrap: 'wrap' }}>
          {QUEUES.map(([key, label]) => (
            <button key={key} type="button" style={tabStyle(queue === key)} onClick={() => pickQueue(key)}>
              {label}{counts[key] !== undefined ? ` (${counts[key]})` : ''}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <input aria-label="Search claims" style={{ ...inputStyle, width: 200 }} placeholder="Client or control #" value={q} onChange={(e) => setQ(e.target.value)} />
          <select aria-label="Therapist" style={{ ...selectStyle, width: 180 }} value={filters.therapistId} onChange={(e) => setFilter({ therapistId: e.target.value })}>
            <option value="">All therapists</option>{therapists.map((t) => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
          </select>
          <select aria-label="Payer" style={{ ...selectStyle, width: 180 }} value={filters.payerId} onChange={(e) => setFilter({ payerId: e.target.value })}>
            <option value="">All payers</option>{payers.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
          </select>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'center' }}>DOS from <input type="date" style={{ ...inputStyle, width: 150 }} value={filters.dosFrom} onChange={(e) => setFilter({ dosFrom: e.target.value })} /></label>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'center' }}>to <input type="date" style={{ ...inputStyle, width: 150 }} value={filters.dosTo} onChange={(e) => setFilter({ dosTo: e.target.value })} /></label>
          {readyRows.length > 0 && <Button variant="success" disabled={busy || selected.size === 0} onClick={submitSelected} style={{ marginLeft: 'auto' }}>Submit {selected.size} selected</Button>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--secondary-color)' }}>
                <th style={th}>{readyRows.length > 0 ? <input type="checkbox" aria-label="Select all ready claims" checked={allReadySelected} onChange={toggleAllReady} /> : ''}</th>
                {columns.map((h) => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan={columns.length + 1} style={{ ...td, color: 'var(--text-muted)' }}>No claims here.</td></tr>}
              {data.items.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border-color)', background: c.attention && c.attention.flag ? 'color-mix(in srgb, var(--error-color) 6%, var(--white))' : undefined }}>
                  <td style={td}>{c.status === 'ready' && <input type="checkbox" aria-label={`Select claim ${c.id}`} checked={selected.has(c.id)} onChange={() => toggle(c.id)} />}</td>
                  <td style={td}><Link to={`/admin/claims/${c.id}`}>{c.client_name}</Link><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.patient_control_number}</div></td>
                  <td style={td}>{formatLongDate(c.dos)}</td>
                  <td style={td}>{c.cpt}</td>
                  <td style={td}>{c.payer_name}</td>
                  <td style={td}>{c.therapist_name}</td>
                  {data.showAmounts && <td style={td}>{c.total_charge === null || c.total_charge === undefined ? '' : formatMoney(c.total_charge)}</td>}
                  <td style={td}>
                    <ClaimStatusBadge status={c.status} statusLabel={c.status_label} />
                    {c.attention && c.attention.flag && <div style={{ fontSize: 11, color: 'var(--error-color)', marginTop: 2 }}>{c.attention.reason}</div>}
                  </td>
                  <td style={td}>{c.days_in_status}</td>
                  <td style={{ ...td, maxWidth: 280 }}>{c.next_action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 10, fontSize: 13 }}>
            <Button variant="subtle" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <span>Page {page} of {pages}</span>
            <Button variant="subtle" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}
      </Card>
    </div>
  );
}

// Completed sessions that can become claims. Rows with problems are listed (so
// the office sees what to fix) but cannot be selected.
function SessionBuilder({ onCreated }) {
  const [days, setDays] = useState(120);
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setRows((await API.get('/billing/claimable-sessions', { params: { days } })).data); setPicked(new Set()); setError(''); }
    catch (err) { setError(apiErrors(err).message); }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const ok = (s) => !s.problems || s.problems.length === 0;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function create() {
    setBusy(true);
    try {
      const r = await API.post('/billing/claims', { sessionIds: [...picked] });
      const { created = [], skipped = [] } = r.data;
      onCreated(created.length, [
        `${plural(created.length, 'claim')} created${created.length ? ' (' + created.map((c) => c.patientControlNumber).join(', ') + ')' : ''}.`,
        ...skipped.map((s) => `Session ${s.sessionId} skipped: ${s.reason}`),
      ]);
      load();
    } catch (err) {
      const data = (err.response && err.response.data) || {};
      onCreated(0, [apiErrors(err).message, ...((data.skipped || []).map((s) => `Session ${s.sessionId}: ${s.reason}`))]);
    } finally { setBusy(false); }
  }
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 15, color: 'var(--primary-color)' }}>Sessions without a claim</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          Last <select aria-label="Look back" style={{ ...selectStyle, width: 110 }} value={days} onChange={(e) => setDays(Number(e.target.value))}>{[30, 60, 90, 120, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}</select>
          <Button variant="primary" disabled={busy || picked.size === 0} onClick={create}>Create {plural(picked.size, 'claim')}</Button>
        </div>
      </div>
      {error && <Notice tone="bad">{error}</Notice>}
      {rows === null ? <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>Loading…</div>
        : rows.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>Every insured session in this window already has a claim.</div>
        : (
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--secondary-color)' }}>{['', 'Date', 'Client', 'Therapist', 'Payer', 'CPT', 'Charge', 'Status'].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.session_id} style={{ borderTop: '1px solid var(--border-color)', opacity: ok(s) ? 1 : 0.85 }}>
                    <td style={td}><input type="checkbox" aria-label={`Select session ${s.session_id}`} disabled={!ok(s)} checked={picked.has(s.session_id)} onChange={() => toggle(s.session_id)} /></td>
                    <td style={td}>{formatLongDate(s.session_date)}</td>
                    <td style={td}>{s.client_name}</td>
                    <td style={td}>{s.therapist_name}</td>
                    <td style={td}>{s.payer_name}</td>
                    <td style={td}>{s.cpt || <span style={{ color: 'var(--text-muted)' }}>none</span>}</td>
                    <td style={td}>{s.charge === null || s.charge === undefined ? '—' : formatMoney(s.charge)}</td>
                    <td style={td}>{ok(s) ? <StatusBadge tone="good">Ready to create</StatusBadge> : <span style={{ color: '#B7791F', fontSize: 12 }}>{describeProblems(s.problems)}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Card>
  );
}
